import { db, getMeta, setMeta, META_KEYS } from "../local/db";
import type { LocalCategory, LocalMenuItem, LocalVariant, LocalStaff } from "../local/db";
import { money } from "../../domain/money/money";

/**
 * Applying server changes to the local replica (T093, T198).
 *
 * Config flows ONE WAY. A terminal never pushes a catalog change, which is what
 * removes the entire class of catalog merge conflicts (research.md R3). So this
 * module only ever writes the replicated tables, and the rule it must never
 * break is stated as code below rather than left to reviewer memory.
 *
 * THE RULE: a pulled change affects SUBSEQUENT sales only (FR-005).
 *
 * A price change that arrives at 3pm must not alter what a 10am receipt says,
 * what the 10am sale reports, or what the shift's expected cash is. This is
 * already structurally true — a sale snapshots its item names and prices into
 * `saleLines` at finalization, so nothing here is even reachable from a
 * committed sale — but "structurally true" decays silently. `FORBIDDEN_TABLES`
 * turns it into a runtime failure instead.
 */

/**
 * Tables a pull may never write.
 *
 * Journal tables. If a change type ever needs to touch one of these, the change
 * type is wrong — corrections are new referencing records, never edits.
 */
const FORBIDDEN_TABLES = new Set([
  "sales",
  "saleLines",
  "tenders",
  "discounts",
  "auditEntries",
  "cashMovements",
]);

export interface ServerChange {
  seq: number;
  type: string;
  payload: unknown;
}

export interface PullOutcome {
  applied: number;
  ignored: number;
  cursor: number;
  /** Change types seen that this build has no handler for. */
  unknownTypes: string[];
}

export class PullIntegrityError extends Error {
  constructor(table: string, type: string) {
    super(
      `Change '${type}' tried to write '${table}', which is journal state. ` +
        `Pulled changes affect subsequent sales only (FR-005).`,
    );
    this.name = "PullIntegrityError";
  }
}

function assertWritable(table: string, type: string): void {
  if (FORBIDDEN_TABLES.has(table)) throw new PullIntegrityError(table, type);
}

type ChangeHandler = (payload: never) => Promise<void>;

const HANDLERS: Record<string, ChangeHandler> = {
  "catalog.item_changed": applyItemChanged as ChangeHandler,
  "catalog.price_changed": applyPriceChanged as ChangeHandler,
  "catalog.availability_changed": applyAvailabilityChanged as ChangeHandler,
  "catalog.category_changed": applyCategoryChanged as ChangeHandler,
  staff_changed: applyStaffChanged as ChangeHandler,
  config_changed: applyConfigChanged as ChangeHandler,
  module_toggled: applyModuleToggled as ChangeHandler,
};

/**
 * Apply a batch of pulled changes and advance the cursor.
 *
 * The cursor advances past an unknown change type rather than stalling on it.
 * An older terminal that cannot understand a new change type must keep syncing
 * its SALES — the thing that actually matters — rather than blocking the whole
 * outbox behind a catalog message it will never handle. Unknown types are
 * reported so the mismatch is visible.
 */
export async function applyChanges(changes: readonly ServerChange[]): Promise<PullOutcome> {
  const outcome: PullOutcome = {
    applied: 0,
    ignored: 0,
    cursor: (await getMeta<number>(META_KEYS.syncCursor)) ?? 0,
    unknownTypes: [],
  };

  for (const change of changes) {
    const handler = HANDLERS[change.type];

    if (!handler) {
      outcome.ignored += 1;
      if (!outcome.unknownTypes.includes(change.type)) {
        outcome.unknownTypes.push(change.type);
      }
    } else {
      await handler(change.payload as never);
      outcome.applied += 1;
    }

    // Advanced per change, not per batch: an error midway must not replay the
    // changes that already landed.
    if (change.seq > outcome.cursor) {
      outcome.cursor = change.seq;
      await setMeta(META_KEYS.syncCursor, change.seq);
    }
  }

  return outcome;
}

// ---------------------------------------------------------------------------
// Handlers
//
// Each writes ONLY replicated tables. `assertWritable` is called with the table
// name so a future edit that reaches for a journal table fails loudly.
// ---------------------------------------------------------------------------

async function applyItemChanged(payload: {
  item: LocalMenuItem;
  variants?: LocalVariant[];
}): Promise<void> {
  assertWritable("menuItems", "catalog.item_changed");
  await db.menuItems.put(payload.item);

  if (payload.variants) {
    await db.variants.bulkPut(payload.variants);
  }
}

/**
 * A price change replaces the price the TILL will offer from now on.
 *
 * It does not, and cannot, reach a sale already rung: `saleLines.unitPrice` is
 * a snapshot written at finalization. That is the whole mechanism behind "an
 * older receipt reprints at the old price".
 */
async function applyPriceChanged(payload: {
  variantId?: string;
  modifierId?: string;
  amount: number;
}): Promise<void> {
  assertWritable("variants", "catalog.price_changed");

  // `money()` is the boundary that stops a float from a server payload ever
  // entering a price field.
  if (payload.variantId) {
    await db.variants.update(payload.variantId, { price: money(payload.amount) });
  }
  if (payload.modifierId) {
    await db.modifiers.update(payload.modifierId, {
      priceAdjustment: money(payload.amount),
    });
  }
}

async function applyAvailabilityChanged(payload: {
  menuItemId: string;
  available: boolean;
}): Promise<void> {
  assertWritable("menuItems", "catalog.availability_changed");
  await db.menuItems.update(payload.menuItemId, { available: payload.available });
}

async function applyCategoryChanged(payload: { category: LocalCategory }): Promise<void> {
  assertWritable("categories", "catalog.category_changed");
  await db.categories.put(payload.category);
}

/**
 * Staff changes are replicated because manager authorization has to work with
 * no network (FR-056). A manager who cannot be verified offline is a manager
 * who cannot authorize a void during an outage.
 */
async function applyStaffChanged(payload: { staff: LocalStaff }): Promise<void> {
  assertWritable("staff", "staff_changed");
  await db.staff.put(payload.staff);
}

async function applyConfigChanged(payload: { profile: Record<string, unknown> }): Promise<void> {
  await setMeta(META_KEYS.businessProfile, payload.profile);
}

async function applyModuleToggled(payload: { modules: string[] }): Promise<void> {
  await setMeta(META_KEYS.activeModules, payload.modules);
}
